// components/footfall/pulseState.ts
import type { useKernel } from '@/kernel/Kernel';
import { ownerOf, venueBounds } from '#lib/footfall/pulse';
import { ignBuildingsUrl, parseIgnBuildings } from '#lib/footfall/volumes';
import { PLAN_NAME_MAX, SCENARIOS_MAX, THESIS_MAX, blankScenario, moveError } from '#lib/footfall/scenario';
import { campaignLabel } from '#lib/leasing/campaignCard';
import type { CampaignDTO, PulseLayoutChange, PulseMove, PulsePlan, PulseRuntime } from '#types/types';
import { childrenOf, parentsOf } from '#lib/footfall/layout';

type Kernel = NonNullable<ReturnType<typeof useKernel>['kernel']['current']>;

/** Scored leads only: a pending, reverted or dismissed row is not a brand Source recommends. */
export const SCORED = ['SUCCESS', 'INTERESTING'] as const;
const PAGE = 500;
const SAVE_FAILED = 'Could not save. Your changes are kept here; retry.';
export const CONFLICT = 'This plan was changed in another tab or window. Your changes here are not saved.';

export const blankRuntime = (ownerKey: string | null): PulseRuntime => ({ ownerKey, planId: null, propertyId: null, selectedUnitId: null, section: 'shops', tool: 'select', shortlist: null, draft: null, view3d: false, agentEdit: null, agentTouched: [], zoom: null, buildings: null, baseRevision: null, editVersion: 0, savedVersion: 0, saving: false, error: null, notice: null });

/** Patches Pulse's working state, only while it still belongs to the signed-in owner. */
export function patchPulse(k: Kernel | null | undefined, patch: Partial<PulseRuntime>) {
  const live = k?.getState().pulse;
  if (k && live?.ownerKey && live.ownerKey === ownerOf(k.getUserContext())) k.setState({ pulse: { ...live, ...patch } });
}

/** Marks the shop the agent is working on, for its cursor on the map, and adds it to the shops it touched this run. */
export function markAgentEdit(k: Kernel | null | undefined, shopId: string) {
  const live = k?.getState().pulse;
  if (!live) return;
  patchPulse(k, { agentEdit: { shopId, at: Date.now() }, agentTouched: live.agentTouched.includes(shopId) ? live.agentTouched : [...live.agentTouched, shopId] });
}

/** Selects a shop and opens its section in the right bar, as selecting a layer in Studio opens its settings. */
export function selectShop(k: Kernel | null | undefined, shopId: string | null, patch: Partial<PulseRuntime> = {}) {
  patchPulse(k, { ...patch, selectedUnitId: shopId, ...(shopId ? { section: 'shop' as const } : {}) });
}

/** Applies an edit to the open plan and marks it unsaved. */
export function editDraft(k: Kernel | null | undefined, edit: (draft: PulsePlan) => PulsePlan) {
  const live = k?.getState().pulse;
  if (!k || !live?.draft) return;
  patchPulse(k, { draft: edit(live.draft), editVersion: live.editVersion + 1, error: null });
}

/** Creates or replaces the move on a shop; a new move replaces the shop's previous one. */
export function setMove(k: Kernel | null | undefined, move: PulseMove) {
  editDraft(k, draft => ({ ...draft, moves: [...draft.moves.filter(m => m.shopId !== move.shopId), move] }));
}
export function removeMove(k: Kernel | null | undefined, shopId: string) {
  editDraft(k, draft => ({ ...draft, moves: draft.moves.filter(m => m.shopId !== shopId) }));
}
/** Updates one move field after validation; returns the refusal, if any, for the caller to show. */
export function updateMove(k: Kernel | null | undefined, shopId: string, patch: Partial<PulseMove>): string | null {
  const move = k?.getState().pulse.draft?.moves.find(m => m.shopId === shopId);
  if (!move) return null;
  const next = { ...move, ...patch };
  const error = moveError(next, undefined, move.start);
  if (error) return error;
  setMove(k, next);
  return null;
}
export const newMove = (shopId: string, patch: Partial<PulseMove> = {}): PulseMove => {
  const start = new Date(); start.setMonth(start.getMonth() + 6); start.setDate(1);
  return { shopId, type: 'relet', brandId: null, brandName: null, brandCategory: null, rentPerSqm: null, start: start.toISOString().slice(0, 10), rentFreeMonths: 0, works: 0, ...patch };
};

/** A plan saved before scenarios existed reads as one scenario holding its moves. */
export const withScenarios = (plan: PulsePlan): PulsePlan => plan.scenarios?.length && plan.activeScenarioId ? plan
  : { ...plan, scenarios: [{ id: 's1', name: 'Scenario 1', thesis: '', moves: [] }], activeScenarioId: 's1' };
export const savedPlans = (k: Kernel | null | undefined): Record<string, PulsePlan> =>
  Object.fromEntries(Object.entries(k?.getUserPreferences().pulsePlans ?? {}).map(([id, plan]) => [id, withScenarios(plan)]));

/** A scenario's moves: the plan's own for the one on screen, its stored ones for the others. */
export const scenarioMoves = (plan: PulsePlan, scenarioId: string): PulseMove[] =>
  scenarioId === plan.activeScenarioId ? plan.moves : plan.scenarios?.find(s => s.id === scenarioId)?.moves ?? [];
export const scenarioLayout = (plan: PulsePlan, scenarioId: string): PulseLayoutChange[] =>
  (scenarioId === plan.activeScenarioId ? plan.layout : plan.scenarios?.find(s => s.id === scenarioId)?.layout) ?? [];

/** Reshapes the scenario on screen; moves on the units it takes away go with them. */
export function addLayoutChange(k: Kernel | null | undefined, change: PulseLayoutChange) {
  const gone = new Set(parentsOf(change));
  editDraft(k, d => ({ ...d, layout: [...(d.layout ?? []), change], moves: d.moves.filter(m => !gone.has(m.shopId)) }));
}
/** Undoes the reshaping that took away or created this unit; moves on the units it had created go with it. */
export function removeLayoutChange(k: Kernel | null | undefined, shopId: string): boolean {
  const change = k?.getState().pulse.draft?.layout?.find(c => parentsOf(c).includes(shopId) || childrenOf(c).includes(shopId));
  if (!change) return false;
  const gone = new Set(childrenOf(change));
  editDraft(k, d => ({ ...d, layout: (d.layout ?? []).filter(c => c !== change), moves: d.moves.filter(m => !gone.has(m.shopId)) }));
  return true;
}

/**
 * Puts a scenario on screen: the current one's moves, layout and Source run go back to its entry, the chosen one's
 * come out of it. Each scenario is a hypothesis built on its own Source run (owner, 2026-10-14: "sourcings are the
 * basis for merch plan hypotheses"); one saved before scenarios carried a run keeps the plan's.
 */
export const activateScenario = (plan: PulsePlan, scenarioId: string): PulsePlan => {
  const stashed = (plan.scenarios ?? []).map(s => s.id === plan.activeScenarioId ? { ...s, moves: plan.moves, layout: plan.layout ?? [], campaignId: plan.campaignId ?? null } : s);
  const next = stashed.find(s => s.id === scenarioId)!;
  return { ...plan, moves: next.moves, layout: next.layout ?? [], campaignId: next.campaignId === undefined ? plan.campaignId ?? null : next.campaignId, scenarios: stashed.map(s => s.id === scenarioId ? { ...s, moves: [], layout: [] } : s), activeScenarioId: scenarioId };
};
/** Creates a scenario, from scratch or as a copy of the one on screen, and puts it on screen. Refuses past the limit. */
export function createScenario(k: Kernel | null | undefined, name: string, thesis: string, from: 'empty' | 'current'): string | { error: string } {
  const draft = k?.getState().pulse.draft;
  if (!draft) return { error: 'No plan is open.' };
  const scenarios = draft.scenarios ?? [];
  if (scenarios.length >= SCENARIOS_MAX) return { error: `A plan holds at most ${SCENARIOS_MAX} scenarios. Replace one rather than adding.` };
  // A plan starts with one empty, unexplained scenario: the agent's first scenario takes it over rather than sitting
  // beside a blank tab and using up a place.
  const only = scenarios.length === 1 ? scenarios[0]! : null;
  if (only && only.id === draft.activeScenarioId && !only.thesis && !draft.moves.length && !draft.layout?.length) {
    editDraft(k, d => ({ ...d, scenarios: [{ ...only, name: name.trim().slice(0, 60) || only.name, thesis: thesis.slice(0, THESIS_MAX) }] }));
    return only.id;
  }
  const id = `s${Date.now().toString(36)}`;
  const entry = { id, name: name.trim().slice(0, 60) || `Scenario ${scenarios.length + 1}`, thesis: thesis.slice(0, THESIS_MAX), moves: from === 'current' ? draft.moves : [], layout: from === 'current' ? draft.layout ?? [] : [], campaignId: draft.campaignId ?? null };
  editDraft(k, d => activateScenario({ ...d, scenarios: [...(d.scenarios ?? []), entry] }, id));
  return id;
}
export function switchScenario(k: Kernel | null | undefined, scenarioId: string) {
  const draft = k?.getState().pulse.draft;
  if (!draft || draft.activeScenarioId === scenarioId || !draft.scenarios?.some(s => s.id === scenarioId)) return;
  editDraft(k, d => activateScenario(d, scenarioId));
  patchPulse(k, { selectedUnitId: null });
  // Another hypothesis can stand on another Source run: its shortlist comes with it.
  const next = k?.getState().pulse.draft;
  if (k && next && next.campaignId !== draft.campaignId) void loadShortlist(k, next.propertyId, next.campaignId ?? null);
}
export function setScenarioThesis(k: Kernel | null | undefined, scenarioId: string, thesis: string) {
  editDraft(k, d => ({ ...d, scenarios: (d.scenarios ?? []).map(s => s.id === scenarioId ? { ...s, thesis: thesis.slice(0, THESIS_MAX) } : s) }));
}

/**
 * Writes the plan map after reloading it, so a write from this tab never drops a plan another tab added. `change`
 * receives the fresh map and returns the next one, or a refusal. The write counts only once read back.
 */
async function writePlans(k: Kernel, change: (plans: Record<string, PulsePlan>) => Record<string, PulsePlan> | string, check: (plans: Record<string, PulsePlan>) => boolean): Promise<string | null> {
  await k.loadUserPreferences();
  const next = change(savedPlans(k));
  if (typeof next === 'string') return next;
  await k.updateAndSaveUserPreference('pulsePlans', next);
  await k.loadUserPreferences();
  return check(savedPlans(k)) ? null : SAVE_FAILED;
}

export const planNameError = (name: string) => !name.trim() ? 'Give the plan a name.' : name.trim().length > PLAN_NAME_MAX ? `A plan name is at most ${PLAN_NAME_MAX} characters.` : null;

export async function createPlan(k: Kernel, propertyId: string, name: string, from?: PulsePlan, campaignId?: string | null): Promise<{ id: string } | { error: string }> {
  const nameError = planNameError(name);
  if (nameError) return { error: nameError };
  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  const plan: PulsePlan = withScenarios({ ...(from ?? blankScenario()), id, name: name.trim(), propertyId, campaignId: campaignId ?? from?.campaignId ?? null, createdAt: now, revision: crypto.randomUUID(), savedAt: now });
  try {
    const error = await writePlans(k, plans => Object.keys(plans).length >= 50 ? 'You have 50 plans, the most one account can keep. Delete one first.' : { ...plans, [id]: plan }, plans => plans[id]?.revision === plan.revision);
    return error ? { error } : { id };
  } catch { return { error: SAVE_FAILED }; }
}

export async function renamePlan(k: Kernel, id: string, name: string): Promise<string | null> {
  const nameError = planNameError(name);
  if (nameError) return nameError;
  const revision = crypto.randomUUID();
  try {
    const error = await writePlans(k, plans => plans[id] ? { ...plans, [id]: { ...plans[id]!, name: name.trim(), revision, savedAt: new Date().toISOString() } } : 'This plan no longer exists.', plans => plans[id]?.revision === revision);
    // The open plan keeps its edits and moves onto the renamed revision.
    const live = k.getState().pulse;
    if (!error && live.planId === id && live.draft) patchPulse(k, { draft: { ...live.draft, name: name.trim() }, baseRevision: revision });
    return error;
  } catch { return SAVE_FAILED; }
}

export async function deletePlan(k: Kernel, id: string): Promise<string | null> {
  try {
    return await writePlans(k, plans => { const { [id]: _gone, ...rest } = plans; return rest; }, plans => !plans[id]);
  } catch { return SAVE_FAILED; }
}

/** Opens a plan in the editor and hydrates what it needs: the centre (venue, footfall, workbook), its units and its shortlist. */
export function openPlan(k: Kernel, id: string) {
  const plan = savedPlans(k)[id];
  if (!plan) return;
  patchPulse(k, { planId: id, propertyId: plan.propertyId, draft: plan, baseRevision: plan.revision, editVersion: 0, savedVersion: 0, selectedUnitId: null, section: 'shops', tool: 'select', error: null, notice: null, saving: false });
  void hydrateCentre(k, plan.propertyId);
  void loadShortlist(k, plan.propertyId, plan.campaignId ?? null);
}
export function closePlan(k: Kernel | null | undefined) {
  patchPulse(k, { planId: null, propertyId: null, draft: null, baseRevision: null, editVersion: 0, savedVersion: 0, selectedUnitId: null, error: null, notice: null, saving: false });
}

/** Leaves the editor for the dashboard, saving first; a failed save keeps the plan open with its error. */
export async function leavePlan(k: Kernel | null | undefined) {
  if (!k) return;
  if (k.getState().pulse.editVersion !== k.getState().pulse.savedVersion) await savePlan(k);
  if (k.getState().pulse.editVersion !== k.getState().pulse.savedVersion) return;
  closePlan(k);
  k.clearActiveChatSession();
}

/** The Brand ids a centre's shops link to, to hydrate before drawing them. */
const venueBrandIds = (venue: { features: Array<{ brandId?: string | null }> } | null | undefined) => [...new Set((venue?.features ?? []).flatMap(f => f.brandId ? [f.brandId] : []))];

/** The centre (venue, footfall, workbook) and the Brand records its shops link to. */
export async function hydrateCentre(k: Kernel, propertyId: string) {
  // One request at a time per centre: the view asks again while the plan is missing, and must not pile requests up.
  if (hydrating.has(propertyId)) return;
  hydrating.add(propertyId);
  try {
    await k.syncEntity('Property', propertyId).catch(() => undefined);
    const brandIds = venueBrandIds(k.getState().inventory.Property.registry[propertyId]?.venue);
    if (brandIds.length) await k.ensureInventoryIds('Brand', brandIds, { force: false, batchSize: 200 });
  } finally {
    hydrating.delete(propertyId);
  }
}
const hydrating = new Set<string>();

/** The IGN buildings around a centre, fetched once per centre, for the 3D view's heights. */
export async function loadBuildings(k: Kernel, propertyId: string) {
  const current = k.getState().pulse.buildings;
  if (current?.propertyId === propertyId && current.status !== 'error') return;
  const venue = k.getState().inventory.Property.registry[propertyId]?.venue;
  const bounds = venue ? venueBounds(venue) : null;
  if (!bounds) return;
  patchPulse(k, { buildings: { propertyId, status: 'loading', list: [] } });
  const settle = (buildings: PulseRuntime['buildings']) => { if (k.getState().pulse.buildings?.propertyId === propertyId) patchPulse(k, { buildings }); };
  try {
    const res = await fetch(ignBuildingsUrl(bounds));
    if (!res.ok) throw new Error(`IGN answered ${res.status}`);
    settle({ propertyId, status: 'ready', list: parseIgnBuildings(await res.json()) });
  } catch {
    settle({ propertyId, status: 'error', list: [] });
  }
}

/** Which shortlist the open plan reads: the account's, for that centre and that Source run (every run when the plan names none). */
export const shortlistKey = (ownerKey: string | null, propertyId: string | null, campaignId: string | null | undefined) => `${ownerKey}|${propertyId}|${campaignId ?? 'all'}`;

/** The asset's scored leads, of one Source run when the plan is tied to one, page by page, with their brands. */
export async function loadShortlist(k: Kernel, propertyId: string, campaignId: string | null = null) {
  const key = shortlistKey(k.getState().pulse.ownerKey, propertyId, campaignId);
  const current = k.getState().pulse.shortlist;
  if (current?.key === key && current.status !== 'error') return;
  patchPulse(k, { shortlist: { key, status: 'loading', ids: [] } });
  const settle = (shortlist: PulseRuntime['shortlist']) => { if (k.getState().pulse.shortlist?.key === key) patchPulse(k, { shortlist }); };
  try {
    const ids: string[] = [];
    for (const status of SCORED) {
      for (let offset = 0; ; offset += PAGE) {
        const res = await k.queryInventory('Lead', { filters: { propertyId, status, sortBy: 'fitScore', sortDir: 'desc', ...(campaignId ? { lastCampaignId: campaignId } : {}) }, limit: PAGE, offset, force: true, ttl: 0 });
        if (res.error) throw new Error(res.error);
        ids.push(...res.ids);
        if (res.ids.length < PAGE) break;
      }
    }
    const brandIds = [...new Set(ids.flatMap(id => { const lead = k.getState().inventory.Lead.registry[id]; return lead?.brandId ? [lead.brandId] : []; }))];
    if (brandIds.length) await k.ensureInventoryIds('Brand', brandIds, { force: false, batchSize: 200 });
    settle({ key, status: 'ready', ids });
  } catch {
    settle({ key, status: 'error', ids: [] });
  }
}

/** Ties the scenario on screen to one Source run's shortlist, and loads it. */
export function setPlanShortlist(k: Kernel | null | undefined, campaignId: string) {
  const live = k?.getState().pulse;
  if (!k || !live?.draft || !live.propertyId || live.draft.campaignId === campaignId) return;
  editDraft(k, d => ({ ...d, campaignId }));
  void loadShortlist(k, live.propertyId, campaignId);
}

/** The asset's Source runs, newest first, for choosing a plan's shortlist. */
export async function loadCentreCampaigns(k: Kernel, propertyId: string): Promise<string[]> {
  const res = await k.queryInventory('Campaign', { filters: { propertyId, sortBy: 'createdAt', sortDir: 'desc' }, limit: 100, offset: 0, force: true });
  if (res.error) throw new Error(res.error);
  return res.ids;
}

/** Replaces the working copy with the latest saved plan, dropping unsaved edits (after a conflict). */
export function loadLatest(k: Kernel | null | undefined) {
  const live = k?.getState().pulse;
  const saved = live?.planId ? savedPlans(k)[live.planId] : undefined;
  if (!saved) return closePlan(k);
  patchPulse(k, { draft: saved, baseRevision: saved.revision, editVersion: 0, savedVersion: 0, error: null, selectedUnitId: null });
}

/**
 * Saves the open plan. The server copy is reloaded first: if another tab saved this plan since it was opened here, the
 * save is refused rather than overwriting it.
 */
export async function savePlan(k: Kernel | null | undefined) {
  const live = k?.getState().pulse;
  if (!k || !live?.draft || !live.planId || live.saving) return;
  const { ownerKey: owner, planId: id, editVersion: version } = live;
  patchPulse(k, { saving: true, error: null });
  const fail = (error: string) => { if (k.getState().pulse.ownerKey === owner) patchPulse(k, { saving: false, error }); };
  const revision = crypto.randomUUID();
  try {
    const error = await writePlans(k, plans => {
      const server = plans[id];
      if (!server) return 'This plan was deleted in another tab or window.';
      if (server.revision !== k.getState().pulse.baseRevision) return CONFLICT;
      const draft = k.getState().pulse.draft!;
      return { ...plans, [id]: { ...draft, revision, savedAt: new Date().toISOString() } };
    }, plans => plans[id]?.revision === revision);
    if (k.getState().pulse.ownerKey !== owner || k.getState().pulse.planId !== id) return;
    if (error) return fail(error);
    patchPulse(k, { saving: false, savedVersion: version, baseRevision: revision, error: null });
  } catch {
    fail(SAVE_FAILED);
  }
}

/** Leaves wherever the user is for Plan, claimed for this user first: Plan resets its state for a user it does not know yet. */
export function enterPlan(k: Kernel) {
  // Leave the chat first: opening a plan, then leaving the session, closed it again.
  k.clearActiveChatSession();
  k.getState().setPortalStandaloneView('footfall');
  const owner = ownerOf(k.getState().userContext);
  if (k.getState().pulse.ownerKey !== owner) k.setState({ pulse: blankRuntime(owner) });
}

/**
 * Opens an asset's merchandising plan from Source, in the window the link opened (owner, 2026-10-14: "a button on the
 * launcher and on the campaign that opens the merch plan in a new window"). With a Source run: the scenario built on
 * it, wherever it is; else a new scenario on it in the asset's newest plan with room (a new plan when every one is
 * full or the asset has none), which is how a sourcing becomes a hypothesis's basis. Without one: the asset's latest plan, else Plan's list. Returns the
 * refusal, if any.
 */
export async function openMerchPlan(k: Kernel, propertyId: string, campaignId: string | null): Promise<string | null> {
  enterPlan(k);
  await k.loadUserPreferences();
  const plans = Object.values(savedPlans(k)).filter(p => p.propertyId === propertyId).sort((a, b) => String(b.savedAt ?? '').localeCompare(String(a.savedAt ?? '')));
  if (!campaignId) {
    if (plans[0]) openPlan(k, plans[0].id);
    return null;
  }
  for (const plan of plans) {
    const scenarioId = plan.campaignId === campaignId ? plan.activeScenarioId : plan.scenarios?.find(s => s.campaignId === campaignId)?.id;
    if (!scenarioId) continue;
    openPlan(k, plan.id);
    switchScenario(k, scenarioId);
    return null;
  }
  await k.syncEntity('Campaign', campaignId).catch(() => undefined);
  const campaign = k.getState().inventory.Campaign.registry[campaignId] as CampaignDTO | undefined;
  const label = campaign ? campaignLabel(campaign, 'Hypothèse') : 'Hypothèse';
  // The newest plan with room for one more scenario; a new plan, built on the run, when every one is full.
  let hostId = plans.find(p => (p.scenarios?.length ?? 1) < SCENARIOS_MAX)?.id;
  if (!hostId) {
    const asset = k.getState().inventory.Property.registry[propertyId]?.name;
    const plan = await createPlan(k, propertyId, (asset ? `${asset} · ${label}` : label).slice(0, PLAN_NAME_MAX), undefined, campaignId);
    if ('error' in plan) return plan.error;
    hostId = plan.id;
  }
  openPlan(k, hostId);
  // A new plan's blank first scenario is taken over and named after the run (createScenario).
  const made = createScenario(k, label, '', 'empty');
  if (typeof made !== 'string') return made.error;
  setPlanShortlist(k, campaignId);
  return null;
}

/** Opens the asset's merch plan in its own window, one per asset, beside the Source page that opened it. */
export function openMerchPlanWindow(propertyId: string, campaignId: string | null) {
  const query = new URLSearchParams({ plan: propertyId, ...(campaignId ? { campaign: campaignId } : {}) });
  window.open(`/?${query.toString()}`, `merch-plan-${propertyId}`, 'popup,width=1440,height=900');
}
